import { useDashboardFilters } from '../../utils/user/dashboardTimeRange';
import React, { useState, useEffect, useCallback, useRef } from 'react';

import SidePanel from '../../components/sidepanel';
import utils from '../../utils';
import MultiDropdown from '../../components/multiDropdown';
import CustomButton from '../../components/customButton';
import Table from '../../components/table';
import columns from './columns';
import Drawer from '../../components/drawer';

import './styles.scss';

import MapCluster from '../../components/mapCluster';
import API from '../../services/api';
import DrawerContent from './drawerContent';
import { useSelector } from 'react-redux';
import { mobileFilters } from './filters';
import { getSelectedProject } from '../../redux/projects/actions';
import EmptyState from '../../components/emptyState';
import { loadAnalyticsSiteMetrics, loadMobileSiteMetrics, normalizeMobileSite } from './siteData';
import { getDiscoveryDateRange } from './dateRange';

const PAGE_SIZE = 15;

const DiscoveryContainer = ({ lastUpdated }) => {
  const project = useSelector(getSelectedProject);

  const [showFilters, setShowFilters] = useState(window.localStorage.getItem('discovery_filter_is_open') === 'true');
  const [hideDetails, setHideDetails] = useState(true);
  const [appliedFilters, setAppliedFilters] = useDashboardFilters(() => ({
    ...getDiscoveryDateRange(),
    timezone: project?.preferences?.timezone
  }));
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [itemsCount, setItemsCount] = useState(0);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedMobileFilters, setSelectedMobileFilters] = useState([]);
  const [lastUpdateTime, setLastUpdateTime] = useState(lastUpdated);
  const siteMetricRequestRef = useRef(0);

  useEffect(() => {
    siteMetricRequestRef.current += 1;
    setLoading(true);
    setSites([]);
    loadAllSites();
    setSelectedSite(null);
    setHideDetails(true);
  }, [selectedMobileFilters, appliedFilters, lastUpdated]);

  const parseMobileFilters = () => {
    const result = [];
    Object.values(selectedMobileFilters).forEach(arr => {
      if (Array.isArray(arr)) {
        result.push(...arr);
      }
    });
    return result;
  };

  const loadAllSites = async () => {
    if (!project || !project.id) return;
    const requestId = siteMetricRequestRef.current;
    const dimension = 'sites';
    const queryObject = {
      startTime: appliedFilters.startDate,
      endTime: appliedFilters.endDate,
      dimension: dimension,
      filters: parseMobileFilters() || [],
      timezone: appliedFilters.timezone || project?.preferences?.timezone || ''
    };

    API.getMobileData(project.id, queryObject)
      .then(response => {
        if (response && response.datasets) {
          handleMobileData(response.datasets, requestId);
        }
      })
      .catch(error => {
        console.error('Error fetching mobile sites:', error);
        setLoading(false);
      });
  };

  const handleMobileData = (sites, requestId) => {
    const mobileSites = sites.map(normalizeMobileSite);
    setSites(mobileSites);
    handleSitePagination(mobileSites);
    loadAnalyticsSiteMetrics({
      projectId: project.id,
      siteIds: mobileSites.map(site => site.siteId).filter(Boolean),
      segmentIds: parseMobileFilters(),
      startTime: appliedFilters.startDate,
      endTime: appliedFilters.endDate,
      timezone: appliedFilters.timezone || project?.preferences?.timezone || '',
      onError: (error, metric, siteId) =>
        console.error(`Error fetching ${metric}${siteId ? ` for site ${siteId}` : ''}:`, error)
    })
      .then(analyticsMetrics => {
        if (requestId !== siteMetricRequestRef.current) return;
        setSites(currentSites =>
          currentSites.map(site => ({
            ...site,
            ...(analyticsMetrics[site.siteId] || {})
          }))
        );
        setTableData(currentSites =>
          currentSites.map(site => ({
            ...site,
            ...(analyticsMetrics[site.siteId] || {})
          }))
        );
      })
      .catch(error => console.error('Error fetching analytics site metrics:', error))
      .finally(() => {
        if (requestId === siteMetricRequestRef.current) setLoading(false);
      });
  };

  const handleSitePagination = sites => {
    setTableData(sites.slice(0, PAGE_SIZE));
    setItemsCount(sites.length);
    setPageCount(Math.ceil(sites.length / PAGE_SIZE));
  };

  const loadSiteMetrics = async site => {
    return loadMobileSiteMetrics({
      getMobileData: API.getMobileData,
      projectId: project.id,
      siteId: site.siteId,
      startTime: appliedFilters.startDate,
      endTime: appliedFilters.endDate,
      timezone: appliedFilters.timezone || project?.preferences?.timezone || '',
      onError: (error, metric) => console.error(`Error fetching ${metric} for site ${site.siteId}:`, error)
    });
  };

  const handleSiteSelection = async site => {
    const requestId = ++siteMetricRequestRef.current;
    const canLoadMetrics = Boolean(project?.id && site?.siteId);
    const loadingSite = { ...site, metricsLoading: canLoadMetrics };
    setSelectedSite(loadingSite);
    setLastUpdateTime(new Date().toISOString());
    setShowFilters(false);
    setHideDetails(false);

    if (!canLoadMetrics) return loadingSite;

    const metrics = await loadSiteMetrics(site);
    if (requestId !== siteMetricRequestRef.current) return null;

    const siteWithMetrics = { ...site, ...metrics, metricsLoading: false };
    setSelectedSite(siteWithMetrics);
    setSites(currentSites =>
      currentSites.map(currentSite => (currentSite.id === site.id ? siteWithMetrics : currentSite))
    );
    return siteWithMetrics;
  };

  const clearSelectedSite = () => {
    siteMetricRequestRef.current += 1;
    setHideDetails(true);
    setSelectedSite(null);
  };

  const openFilterDrawer = isOpen => {
    let keepClosed = isOpen;
    if (hideDetails) {
      keepClosed = true;
    }

    setHideDetails(keepClosed);
    setShowFilters(isOpen);
    window.localStorage.setItem('discovery_filter_is_open', isOpen);
  };

  const onFilterSelect = payload => {
    const newAppliedFilters = utils.deepClone(appliedFilters);
    Object.keys(payload).forEach(key => {
      newAppliedFilters[key] = payload[key];
    });
    setAppliedFilters(newAppliedFilters);
  };

  // Fix fetchData to paginate correctly using a ref to avoid stale closures
  const fetchData = useCallback(({ pageIndex, pageSize, sortBy }) => {
    if (!sitesRef.current || sitesRef.current.length === 0) return;

    const sortedSites = [...sitesRef.current];
    const activeSort = sortBy?.find(sort => sort.id === 'matchRate');
    if (activeSort) {
      sortedSites.sort((siteA, siteB) => {
        const matchRateA = siteA.matchRate ?? -Infinity;
        const matchRateB = siteB.matchRate ?? -Infinity;
        const result = matchRateA - matchRateB;
        return activeSort.desc ? result * -1 : result;
      });
    }

    const startRow = pageIndex * pageSize;
    const endRow = startRow + pageSize;
    setTableData(sortedSites.slice(startRow, endRow));
  }, []);

  const renderTopBar = () => (
    <div className="filter-bar">
      <div className="d-flex button-group">
        <CustomButton classes="btn-primary" title="Generate Report" disabled={true} />
      </div>
    </div>
  );

  const onMobileFilterSelect = (options, filter) => {
    const selectedValues = options.map(option => option.value);
    setSelectedMobileFilters(prev => ({
      ...prev,
      [filter.id]: selectedValues
    }));
    if (selectedValues.length === 0) {
      setSelectedMobileFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[filter.id];
        return newFilters;
      });
    }
    onFilterSelect({ [filter.id]: selectedValues });
  };

  const renderFilters = () => {
    return (
      <div className="d-grid flex-column">
        {mobileFilters.map(filter => {
          return (
            <MultiDropdown
              key={filter.id}
              label={filter.displayName}
              options={filter.options}
              placeholder={`Select ${filter.displayName}...`}
              onChange={options => onMobileFilterSelect(options, filter)}
              defaultValue={
                selectedMobileFilters[filter.id]
                  ? filter.options.filter(option => selectedMobileFilters[filter.id].includes(option.value))
                  : []
              }
              outline
            />
          );
        })}
      </div>
    );
  };

  // Ref to store the closeInfoWindow function from MapCluster
  const closeInfoWindowRef = useRef(null);
  const sitesRef = useRef([]);

  useEffect(() => {
    sitesRef.current = sites;
  }, [sites]);

  const renderTable = () => {
    return (
      <div id="discovery-table">
        <Table
          columns={columns}
          data={tableData}
          fetchData={fetchData}
          loading={loading}
          pageCount={pageCount}
          itemsCount={itemsCount}
          dataLabel={'sites'}
          pageSize={PAGE_SIZE}
          isItemDetailsClosed={!showFilters}
          onItemSelected={handleSiteSelection}
        />
      </div>
    );
  };

  const renderEmptyTable = () => {
    return (
      <EmptyState
        icon={'location'}
        title={'No Sites found'}
        details={'No sites matches the filters selected. Try again with different filters.'}
      />
    );
  };

  return (
    <div className="parent-container discovery-container">
      <div id="discovery-widget-container" className="d-flex flex-column">
        {renderTopBar()}
        <div className={`d-flex`}>
          <SidePanel content={renderFilters()} isOpen={showFilters} onToggle={openFilterDrawer} />
          <div className={`scrollable-container ${showFilters || !hideDetails ? ' split-view' : ''}`}>
            <div className="widgets-container">
              <MapCluster
                sites={sites}
                handleClick={site => handleSiteSelection(site)}
                selectedSite={selectedSite}
                onClose={fn => {
                  closeInfoWindowRef.current = fn;
                }}
                onInfoWindowClose={() => {
                  clearSelectedSite();
                }}
              />
              {loading ? renderTable() : sites?.length > 0 ? renderTable() : renderEmptyTable()}
            </div>
          </div>
          <Drawer
            title={selectedSite?.displayName || '--'}
            hidden={hideDetails}
            onClose={() => {
              clearSelectedSite();
              if (closeInfoWindowRef.current) {
                closeInfoWindowRef.current();
              }
            }}
            body={
              <DrawerContent
                item={selectedSite}
                lastUpdated={lastUpdateTime}
                appliedFilters={{ ...appliedFilters, filters: parseMobileFilters() }}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export default DiscoveryContainer;
